import { Dialog, Transition } from '@headlessui/react';
import { ArrowRightLeft, Gem } from 'lucide-react';

import { Fragment, useId, useState } from 'react';

import {
  type NamedUser,
  type ProSheetVariant,
  type ReplaceCandidate,
  formatNameList,
  formatProMovableAt,
  isProSlotLocked,
} from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import { Alert, Button } from '@splitfiesta/ui';

import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';

import { SupporterBadge } from './SupporterBadge';

export interface ProGroupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  groupName: string;
  variant: ProSheetVariant;
  /** A group that is not Pro yet gets the explainer that does not claim it is. */
  groupIsPro: boolean;
  isOnline: boolean;
  busy: boolean;
  /** The Pro groups list is being fetched again after a failure. */
  retrying?: boolean;
  /** The translated server refusal, e.g. the cooldown with its date. */
  serverMessage?: string;
  onAdd: (replaceGroupId?: string) => void;
  onRemove: () => void;
  onRetry: () => void;
  replaceCandidates: ReplaceCandidate[];
}

// Inside the panel so its clock and confirm state start fresh on every open:
// the header that owns the dialog stays mounted for as long as the page is.
function ProGroupDialogContent({
  onClose,
  groupName,
  variant,
  groupIsPro,
  isOnline,
  busy,
  retrying = false,
  serverMessage,
  onAdd,
  onRemove,
  onRetry,
  replaceCandidates,
}: ProGroupDialogProps) {
  const { t, i18n } = useTranslation();
  // The candidate outlives the open flag so the confirm keeps its sentence
  // through the dialog's leave transition.
  const [replaceFrom, setReplaceFrom] = useState<ReplaceCandidate | null>(null);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [now] = useState(() => new Date().toISOString());
  const offlineHintId = useId();
  const lockedHintId = useId();
  const backupHintId = useId();

  const actionsDisabled = !isOnline || busy;
  const offlineHint = t('groups:pro.offlineDisabled');
  const offlineTitle = isOnline ? undefined : offlineHint;
  const joinNames = (users: NamedUser[]) =>
    formatNameList(
      users.map(user => user.name),
      i18n.language
    );
  /** Null when the slot is free or the date cannot be shown. */
  const lockedLabel = (movableAt: string) => {
    if (!isProSlotLocked(movableAt, now)) return null;
    const date = formatProMovableAt(movableAt, i18n.language, now);
    return date ? t('groups:pro.sheet.removeLocked', { date }) : null;
  };
  const describedBy = (...ids: Array<string | false>) =>
    ids.filter(Boolean).join(' ') || undefined;

  const explainer = (
    <p className="text-sm text-gray-600 dark:text-gray-300">
      {t(
        groupIsPro
          ? 'groups:pro.sheet.explainer'
          : 'groups:pro.sheet.explainerAdd'
      )}
    </p>
  );

  const renderBody = () => {
    switch (variant.kind) {
      case 'loading':
        return (
          <div
            className="flex justify-center py-6"
            data-testid="pro-dialog-loading"
            role="status"
          >
            <div aria-hidden="true" className="loading-spinner w-6 h-6" />
            <span className="sr-only">{t('common:actions.loading')}</span>
          </div>
        );

      case 'error':
        return (
          <>
            <Alert type="error" message={t('errors:api.status.default')} />
            <Button
              variant="outline"
              onClick={onRetry}
              loading={retrying}
              className="w-full min-h-[44px]"
              data-testid="pro-dialog-retry"
            >
              {t('common:errors.retry')}
            </Button>
          </>
        );

      case 'member':
        return (
          <>
            {variant.supporters.length > 0 && (
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('groups:pro.sheet.thanksTo', {
                  names: joinNames(variant.supporters),
                })}
                <SupporterBadge className="ml-1" />
              </p>
            )}
            {explainer}
          </>
        );

      case 'canAdd': {
        const full = variant.slots.used >= variant.slots.total;
        const backup = variant.alreadyBy.length > 0;
        return (
          <>
            {backup && (
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('groups:pro.sheet.alreadyBy', {
                  names: joinNames(variant.alreadyBy),
                })}
              </p>
            )}
            {explainer}
            <div className="space-y-1">
              <Button
                variant="primary"
                onClick={() => onAdd()}
                disabled={actionsDisabled}
                loading={busy}
                title={offlineTitle}
                aria-describedby={describedBy(
                  backup && backupHintId,
                  !isOnline && offlineHintId
                )}
                data-testid="pro-dialog-add"
                className="w-full min-h-[44px]"
                leftIcon={<Gem aria-hidden="true" className="w-4 h-4" />}
              >
                {backup
                  ? t('groups:pro.sheet.addAsBackup')
                  : t('groups:pro.sheet.makePro', variant.slots)}
              </Button>
              {backup && (
                <>
                  <p
                    id={backupHintId}
                    className="text-xs text-center text-gray-500 dark:text-gray-400"
                  >
                    {t('groups:pro.sheet.backupHint')}
                  </p>
                  <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                    {t('groups:pro.sheet.slotsUsed', variant.slots)}
                  </p>
                </>
              )}
            </div>
            {full && replaceCandidates.length > 0 && (
              <section className="space-y-2" data-testid="pro-dialog-replace">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t('groups:pro.sheet.replaceTitle')}
                </h3>
                <ul className="space-y-2">
                  {replaceCandidates.map(candidate => {
                    const locked = variant.firstMoveAvailable
                      ? null
                      : lockedLabel(candidate.movableAt);
                    return (
                      <li key={candidate.groupId}>
                        <button
                          type="button"
                          onClick={() => {
                            setReplaceFrom(candidate);
                            setReplaceOpen(true);
                          }}
                          disabled={actionsDisabled}
                          title={offlineTitle}
                          aria-describedby={describedBy(
                            !isOnline && offlineHintId
                          )}
                          className="w-full flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                        >
                          <ArrowRightLeft
                            aria-hidden="true"
                            className="w-4 h-4 mt-0.5 shrink-0 text-orange-600 dark:text-orange-400"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                              {candidate.groupName}
                            </span>
                            {locked && (
                              <span className="block text-xs text-gray-500 dark:text-gray-400">
                                {locked}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </>
        );
      }

      case 'holder': {
        const locked = isProSlotLocked(variant.movableAt, now);
        const label = lockedLabel(variant.movableAt);
        return (
          <>
            {explainer}
            {label && (
              <p
                id={lockedHintId}
                className="text-sm text-gray-600 dark:text-gray-300"
              >
                {label}
              </p>
            )}
            <Button
              variant="outline"
              onClick={() => setConfirmRemove(true)}
              disabled={actionsDisabled || locked}
              loading={busy}
              title={offlineTitle}
              aria-describedby={describedBy(
                !!label && lockedHintId,
                !isOnline && offlineHintId
              )}
              data-testid="pro-dialog-remove"
              className="w-full min-h-[44px] text-red-600 dark:text-red-400"
            >
              {t('groups:pro.sheet.remove')}
            </Button>
          </>
        );
      }
      default: {
        const unreachable: never = variant;
        return unreachable;
      }
    }
  };

  const hasActions = variant.kind === 'canAdd' || variant.kind === 'holder';

  return (
    <>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-orange-100 dark:bg-orange-900/30">
          <Gem
            aria-hidden="true"
            className="h-5 w-5 text-orange-600 dark:text-orange-400"
          />
        </div>
        <div className="min-w-0">
          <Dialog.Title
            as="h2"
            className="text-lg font-semibold text-gray-900 dark:text-gray-100"
          >
            {t('groups:pro.sheet.title')}
          </Dialog.Title>
          <p className="text-sm text-gray-500 dark:text-gray-400 break-words">
            {groupName}
          </p>
        </div>
      </div>

      {renderBody()}

      {hasActions && !isOnline && (
        <p
          id={offlineHintId}
          className="text-xs text-gray-500 dark:text-gray-400"
        >
          {offlineHint}
        </p>
      )}

      {hasActions && serverMessage && (
        <Alert type="error" message={serverMessage} />
      )}

      <div className="flex justify-end">
        <Button variant="outline" onClick={onClose} className="min-h-[44px]">
          {t('common:actions.close')}
        </Button>
      </div>

      <ConfirmationDialog
        isOpen={replaceOpen}
        onClose={() => setReplaceOpen(false)}
        onConfirm={() => {
          if (replaceFrom) onAdd(replaceFrom.groupId);
          setReplaceOpen(false);
        }}
        title={t('groups:pro.sheet.replaceTitle')}
        // The "7 days" is static copy for this phase; it mirrors the
        // app-config `moveCooldownDays` default and is not interpolated.
        description={t('groups:pro.confirm.replace', {
          fromGroup: replaceFrom?.groupName,
          groupName,
        })}
        confirmText={t('common:actions.confirm')}
        cancelText={t('common:actions.cancel')}
        isLoading={busy}
      />
      <ConfirmationDialog
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={() => {
          onRemove();
          setConfirmRemove(false);
        }}
        title={t('groups:pro.sheet.remove')}
        description={t('groups:pro.confirm.remove', { groupName })}
        confirmText={t('groups:pro.sheet.remove')}
        cancelText={t('common:actions.cancel')}
        variant="danger"
        isLoading={busy}
      />
    </>
  );
}

export function ProGroupDialog(props: ProGroupDialogProps) {
  const { isOpen, onClose } = props;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300 motion-reduce:transition-none"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200 motion-reduce:transition-none"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300 motion-reduce:transition-none"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200 motion-reduce:transition-none"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-md transform rounded-2xl bg-white dark:bg-gray-800 p-4 sm:p-6 text-left shadow-xl transition-all space-y-4"
                data-testid="pro-group-dialog"
              >
                <ProGroupDialogContent {...props} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
